import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CalendarRange, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { NoProjectAccess } from "@/components/AppShell";
import { EnergyKpiStrip } from "@/components/energy/EnergyKpiStrip";
import { EnergyTrendChart } from "@/components/energy/EnergyTrendChart";
import { BaselinesPanel } from "@/components/energy/BaselinesPanel";
import { SiteBreakdownTable } from "@/components/energy/SiteBreakdownTable";

const RANGES = [1, 7, 30];
const RANGE_TRANSITION = { transition: "color .2s ease, border-color .2s ease" };

export default function EnergyPage() {
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(7);
  const { project } = useProject();
  const { user: me } = useAuth();
  const noAccess = me?.role !== "admin" && (me?.projects || []).length === 0;

  useEffect(() => {
    api
      .get("/energy/summary", { params: project ? { project } : {} })
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary({ sites: [] }));
  }, [project]);

  if (noAccess) return <NoProjectAccess />;

  return (
    <div className="max-w-7xl fade-up">
      <div className="k-label flex items-center gap-2">
        <Zap className="h-3.5 w-3.5" /> Energy Management
      </div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]">Energy</h1>
      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <p className="text-[#4D4D4D] text-sm max-w-2xl">
          Live electric demand and consumption across your metered portfolio.
        </p>
        <Link to="/energy/annual" className="k-btn-outline" data-testid="energy-annual-link">
          <CalendarRange className="h-3 w-3" /> Annual rollup
        </Link>
      </div>

      <EnergyKpiStrip summary={summary} />

      <div className="mt-8 flex items-center gap-2">
        <span className="k-sublabel mr-1">Range</span>
        {RANGES.map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`energy-range-${d}d`}
            className={`px-3 py-1 text-xs font-heading font-semibold rounded-md border ${days === d ? "border-[#156F5B] bg-[#156F5B]/10 text-[#156F5B]" : "border-[#E5E7EB] bg-white text-[#6B7280] hover:text-[#212121]"}`}
            style={RANGE_TRANSITION}
          >
            {d}d
          </button>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <EnergyTrendChart kind="demand" days={days} />
        <EnergyTrendChart kind="energy" days={days} />
      </div>

      <BaselinesPanel days={days} />

      <SiteBreakdownTable summary={summary} />
    </div>
  );
}
